import Address from '@components/address';
import Logo from '@components/logo';
import Link from 'next/link';
import type { ReactElement } from 'react';
import React, { useMemo } from 'react';
import type ContactInformation from 'types/contact-information';
import type { SiteSettings } from 'types/site-settings';
import { validateString } from 'utils/validate-string';

import styles from './footer.module.scss';

const rootClass = validateString(styles.root);
const contentClass = validateString(styles.content);
const copyrightClass = validateString(styles.copyright);
const logoContainerClass = validateString(styles.logoContainer);
const menuContainerClass = validateString(styles.menuContainer);
const sponsorContainerClass = validateString(styles.sponsorContainer);

interface Props {
  readonly contactInformation: ContactInformation;
  readonly siteSettings: SiteSettings;
}

function Footer({ contactInformation, siteSettings }: Props): ReactElement {
  const copyrightText = useMemo(
    () => `© ${new Date().getFullYear()} ${siteSettings.title}`,
    [siteSettings],
  );

  return (
    <footer className={rootClass}>
      <div className={contentClass}>
        <div className={logoContainerClass}>
          <Logo
            image={
              typeof siteSettings.logo !== 'undefined' &&
              siteSettings.logo !== null
                ? {
                    src: siteSettings.logo.url,
                    height: siteSettings.logo.dimensions.height,
                    width: siteSettings.logo.dimensions.width,
                  }
                : undefined
            }
            text={
              typeof siteSettings.logo === 'undefined'
                ? siteSettings.title
                : undefined
            }
          />
        </div>
        <div className={menuContainerClass}>
          <div>
            <h2>Explore</h2>
            <ul>
              <li>
                <Link href="/">Home</Link>
              </li>
              <li>
                <Link href="/team">Our Team</Link>
              </li>
              <li>
                <Link href="/policies">Policies</Link>
              </li>
              <li>
                <Link href="/contact">Contact Us</Link>
              </li>
              <li>
                <Link href="/careers">Careers</Link>
              </li>
            </ul>
          </div>
          <div>
            <h2>Visit</h2>
            <ul>
              <li>
                <Address
                  directions
                  {...contactInformation.address}
                  businessName={siteSettings.title}
                />
              </li>
            </ul>

            <h2 style={{ marginTop: '1.6rem' }}>Contact</h2>
            <ul>
              <li>
                <a href={`tel:${contactInformation.phone}`}>
                  {contactInformation.phone}
                </a>
              </li>
              <li>
                <a href={`mailto:${contactInformation.email}`}>
                  {contactInformation.email}
                </a>
              </li>
            </ul>
          </div>
          <div>
            <h2>Follow</h2>
            <ul>
              {typeof contactInformation.instagram !== 'undefined' && (
                <li>
                  <Link href={contactInformation.instagram}>Instagram</Link>
                </li>
              )}
              {typeof contactInformation.facebook !== 'undefined' && (
                <li>
                  <Link href={contactInformation.facebook}>Facebook</Link>
                </li>
              )}
            </ul>
          </div>
        </div>
        <div className={sponsorContainerClass}>
          <Logo
            image={
              typeof siteSettings.logo !== 'undefined' &&
              siteSettings.logo !== null
                ? {
                    src: 'https://cdn.sanity.io/images/olx759io/production/cd938306e6bdb2183f62f1adb31a7d524a05a711-71x45.svg',
                    height: 20,
                    width: siteSettings.logo.dimensions.width,
                  }
                : undefined
            }
            text={
              typeof siteSettings.logo === 'undefined'
                ? siteSettings.title
                : undefined
            }
          />
        </div>
      </div>
      <div>
        <span className={copyrightClass}>
          {copyrightText} - Website by{' '}
          <Link href="https://koennbecker.com">Koenn Becker</Link>
        </span>
      </div>
    </footer>
  );
}

export default Footer;
